import React, { useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

const TIMEOUT_DELAY = 2500;

type CopyButtonProps = {
	copyText: string | null | undefined;
	copyLabel: string;
	copiedLabel: string;
	testId?: string;
};

const buttonStyles = cssMap({
	root: {
		borderRadius: token('radius.small'),
		height: `16px`,
		width: `16px`,
		paddingInlineEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
	},
	cursorDisabledStyles: {
		cursor: 'default',
		border: 'none',
	},
});

export const CopyButton = ({
	copyText,
	copyLabel,
	copiedLabel,
	testId,
}: CopyButtonProps): React.JSX.Element => {
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const [currentTooltipLabel, setCurrentTooltipLabel] = useState(copyLabel);
	const updateTooltip = React.useRef<() => void>();

	React.useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [currentTooltipLabel]);

	const copyTextToClipboard = async () => {
		try {
			if (!copyText) {
				return;
			}

			await navigator.clipboard.writeText(copyText);
			setIsCopied(true);
			setCurrentTooltipLabel(copiedLabel);
			setTimeout(() => {
				setIsCopied(false);
				setCurrentTooltipLabel(copyLabel);
			}, TIMEOUT_DELAY);
		} catch (_e) {
			// Failed to copy
			throw new Error('Failed to copy');
		}
	};

	return (
		<Tooltip
			content={({ update }) => {
				updateTooltip.current = update;
				return currentTooltipLabel;
			}}
			position="top"
		>
			<Pressable
				xcss={cx(buttonStyles.root, isCopied ? buttonStyles.cursorDisabledStyles : undefined)}
				isDisabled={isCopied}
				onClick={copyTextToClipboard}
				aria-label={copyLabel}
				testId={testId}
			>
				{isCopied ? (
					<SuccessIcon label={copiedLabel} color={token('color.icon.accent.green')} />
				) : (
					<CopyIcon label={copyLabel} color={token('color.icon.subtle')} />
				)}
			</Pressable>
		</Tooltip>
	);
};
